import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest, getErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DateInput from "@/components/date-input";
import { ScheduleTimePicker } from "@/components/schedule-time-picker";
import { localDateTimeToUTC } from "@/lib/timezone-schedule";
import type { TeacherProfile } from "@contracts/teacher";
import type { Student } from "@contracts/student";

interface ScheduleEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
  students: Student[];
  preselectedStudentId?: string;
  requireStudentSelection?: boolean;
}

function getDefaultFutureDateTime() {
  const now = new Date();
  const d = new Date(now.getTime() + 30 * 60 * 1000);
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  const snapped = Math.ceil(minutes / 15) * 15;
  d.setMinutes(snapped === 60 ? 0 : snapped);
  if (snapped === 60) d.setHours(d.getHours() + 1);
  return {
    date: d.toISOString().split("T")[0],
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
}

export default function ScheduleEventDialog({
  open,
  onOpenChange,
  trigger,
  students,
  preselectedStudentId,
  requireStudentSelection = false,
}: ScheduleEventDialogProps) {
  const { toast } = useToast();
  const { data: profile } = useQuery<TeacherProfile>({
    queryKey: ["/api/profile"],
  });
  const teacherTz = useMemo(
    () => profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    [profile?.timezone]
  );

  const [studentId, setStudentId] = useState(preselectedStudentId ?? "");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(60);
  const [lessonType, setLessonType] = useState<"online" | "offline">("online");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    const defaults = getDefaultFutureDateTime();
    setDate(defaults.date);
    setTime(defaults.time);
    setDuration(60);
    setNotes("");
    setStudentId(preselectedStudentId ?? "");

    const linkedStudent = students.find((s) => s.id === preselectedStudentId);
    if (linkedStudent) {
      setTitle(linkedStudent.name);
      setLessonType(linkedStudent.lessonType as "online" | "offline");
      setAddress(linkedStudent.address ?? "");
    } else {
      setTitle("");
      setLessonType("online");
      setAddress("");
    }
  }, [open, preselectedStudentId, students]);

  useEffect(() => {
    const linkedStudent = students.find((s) => s.id === studentId);
    if (!linkedStudent) return;
    if (!title.trim()) setTitle(linkedStudent.name);
    setLessonType(linkedStudent.lessonType as "online" | "offline");
    setAddress(linkedStudent.address ?? "");
    setDuration(linkedStudent.lessonDuration || 60);
  }, [studentId, students]); // eslint-disable-line react-hooks/exhaustive-deps

  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await apiRequest("POST", "/api/calendar/events", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/events"] });
      onOpenChange(false);
      toast({ title: "Event scheduled", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({
        title: getErrorMessage(err, "Could not schedule event"),
        variant: "destructive",
      });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (requireStudentSelection && !studentId) {
      toast({ title: "Select a student", variant: "warning" });
      return;
    }
    const [yy, mo, dd] = date.split("-").map(Number);
    const [th, tm] = time.split(":").map(Number);
    const scheduledDate = localDateTimeToUTC(yy, mo, dd, th, tm, teacherTz);
    if (scheduledDate.getTime() <= Date.now()) {
      toast({ title: "Only future events are allowed", variant: "warning" });
      return;
    }
    createMutation.mutate({
      studentId: studentId || null,
      title: title.trim() || null,
      notes: notes.trim() || null,
      scheduledDate: scheduledDate.toISOString(),
      duration,
      lessonType,
      address: lessonType === "offline" ? address || null : null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-sm" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Schedule new event</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {requireStudentSelection && (
            <div className="space-y-2">
              <Label>Student</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger data-testid="select-event-student">
                  <SelectValue placeholder="Select student" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="event-title">Title</Label>
            <Input
              id="event-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title (optional)"
              data-testid="input-event-title"
            />
          </div>
          <div className="space-y-2">
            <Label>Date</Label>
            <DateInput value={date} onChange={setDate} required data-testid="input-event-date" />
          </div>
          <div className="space-y-2">
            <Label>Time</Label>
            <ScheduleTimePicker value={time} onChange={setTime} data-testid="input-event-time" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="event-duration">Duration (minutes)</Label>
            <Input
              id="event-duration"
              type="number"
              min="5"
              max="300"
              step="5"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value, 10) || 60)}
              data-testid="input-event-duration"
            />
          </div>
          <div className="space-y-2">
            <Label>Lesson type</Label>
            <Select value={lessonType} onValueChange={(v) => setLessonType(v as "online" | "offline")}>
              <SelectTrigger data-testid="select-event-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {lessonType === "offline" && (
            <div className="space-y-2">
              <Label htmlFor="event-address">Address</Label>
              <Input
                id="event-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Event address"
                data-testid="input-event-address"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="event-notes">Notes</Label>
            <Input
              id="event-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              data-testid="input-event-notes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending} data-testid="button-save-event">
              {createMutation.isPending ? "Saving..." : "Schedule event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
